// components/common/RichTextEditor.jsx
import React, { useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import uploadEditorImage from '../../utils/uploadEditorImage';
import {
  Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon,
  Eraser, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Undo2, Redo2, X, Pilcrow, Highlighter, Strikethrough,
  Quote, Code, FileText, Table,
  ChevronDown, Image as ImageIcon, Type, Heading as HeadingIcon,
  AlignVerticalJustifyStart
} from 'lucide-react';

const editorStyles = `
  .rich-text-editor-content {
    word-wrap: break-word;
    overflow-wrap: break-word;
    outline: none;
    line-height: 1.5;
    white-space: normal !important;
    font-family: 'Aptos', 'Arial', sans-serif;
    color: #1a1a1a;
  }
  .rich-text-editor-content p { margin: 0; padding: 0; min-height: 1.1em; }
  .rich-text-editor-content [style*="background-color"],
  .rich-text-editor-content [style*="background:"] { padding: 1px 3px; border-radius: 2px; }
  .rich-text-editor-content table { border-collapse: collapse; width: 100% !important; margin: 10px 0; }
  .rich-text-editor-content td, .rich-text-editor-content th {
    border: 1px solid #ddd; padding: 8px; vertical-align: top;
  }
  .rich-text-editor-content ul, .rich-text-editor-content ol { padding-left: 2rem; margin: 8px 0; }
  .rich-text-editor-content ul { list-style-type: disc; }
  .rich-text-editor-content ol { list-style-type: decimal; }
  .rich-text-editor-content blockquote { border-left: 4px solid #3b82f6; padding-left: 1rem; font-style: italic; color: #4b5563; }
  .rich-text-editor-content pre { background: #f4f4f5; padding: 12px; border-radius: 6px; font-family: monospace; overflow-x: auto; }
  .rich-text-editor-content code { background: #f4f4f5; padding: 2px 4px; border-radius: 4px; font-family: monospace; }
  .rich-text-editor-content img { max-width: 100%; height: auto; }
  .rich-text-editor-content sup { font-size: 0.75em; vertical-align: super; line-height: 0; }
  .rich-text-editor-content sub { font-size: 0.75em; vertical-align: sub; line-height: 0; }
  .rich-text-editor-content:empty:before { content: attr(data-placeholder); color: #9ca3af; }
  .rich-text-editor-content .rte-image-placeholder {
    display: inline-block;
    padding: 4px 8px;
    background: #eef2ff;
    border: 1px dashed #6366f1;
    border-radius: 4px;
    font-size: 12px;
    color: #4338ca;
  }
  .rich-text-editor-content.rte-dragover {
    outline: 2px dashed #6366f1;
    outline-offset: -4px;
    background: #f5f3ff;
  }
  .rich-text-editor-content.rte-limit-reached {
    box-shadow: inset 0 0 0 2px #fecaca;
  }
`;

// --------------------- SANITIZER CONSTANTS ---------------------
const DANGEROUS_TAGS = new Set([
  'SCRIPT', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'INPUT',
  'BUTTON', 'TEXTAREA', 'SELECT', 'OPTION', 'LINK', 'META',
  'BASE', 'STYLE', 'TITLE', 'XML', 'APPLET', 'FRAME', 'FRAMESET',
  'NOSCRIPT', 'TEMPLATE'
]);

const ALLOWED_STYLE_PROPS = new Set([
  'background-color', 'color', 'text-align', 'margin-left', 'margin-right',
  'margin-top', 'margin-bottom', 'padding', 'padding-top', 'padding-right',
  'padding-bottom', 'padding-left', 'font-size', 'font-family', 'font-weight',
  'font-style', 'text-decoration', 'line-height', 'border', 'border-collapse',
  'border-color', 'border-width', 'border-style', 'width', 'height',
  'vertical-align', 'list-style-type', 'white-space'
]);

// --------------------- SANITIZER HELPERS ---------------------
const isDarkColor = (color) => {
  if (!color) return false;
  const c = String(color).toLowerCase();
  return c.includes('blue') || c.includes('navy') ||
         c.includes('#000080') || c.includes('#00008b') ||
         c.includes('black') || c.includes('purple');
};

const extractExactColor = (colorValue) => {
  if (!colorValue) return null;
  const raw = String(colorValue).trim().replace(/['"]/g, '');
  if (/^#[0-9A-Fa-f]{3,8}$/.test(raw)) return raw;
  if (/^rgb(a)?\(/i.test(raw)) return raw;
  const colorMap = {
    'darkblue': '#00008b', 'navy': '#000080', 'navyblue': '#000080',
    'dark navy': '#000080', 'blue': '#0000ff', 'black': '#000000',
    'white': '#ffffff', 'red': '#ff0000', 'green': '#00ff00',
    'yellow': '#ffff00', 'gray': '#808080', 'grey': '#808080',
    'purple': '#800080', 'darkpurple': '#4b0082', 'orange': '#ffa500',
    'pink': '#ffc0cb', 'brown': '#a52a2a', 'cyan': '#00ffff',
    'magenta': '#ff00ff', 'lime': '#00ff00', 'teal': '#008080',
    'silver': '#c0c0c0', 'maroon': '#800000', 'olive': '#808000'
  };
  const lower = raw.toLowerCase();
  if (lower === 'windowtext' || lower === 'auto' || lower === 'transparent') return null;
  return colorMap[lower] || null;
};

const parseStyleAttr = (styleStr) => {
  const out = {};
  if (!styleStr) return out;
  styleStr.split(';').forEach(part => {
    const idx = part.indexOf(':');
    if (idx === -1) return;
    const prop = part.slice(0, idx).trim().toLowerCase();
    const val = part.slice(idx + 1).trim();
    if (prop && val) out[prop] = val;
  });
  return out;
};

const buildStyleString = (styleObj) => {
  return Object.entries(styleObj)
    .filter(([prop, val]) => {
      if (!ALLOWED_STYLE_PROPS.has(prop)) return false;
      if (!val) return false;
      const v = String(val).toLowerCase();
      if (v.includes('javascript:') || v.includes('expression(') || v.includes('url(')) return false;
      return true;
    })
    .map(([prop, val]) => `${prop}: ${val}`)
    .join('; ');
};

// --------------------- COMPONENT ---------------------
const RichTextEditor = forwardRef(({
  value = '',
  onChange,
  onHtmlChange,          // (html: string) => void — optional
  onWordCountChange,     // (count: number) => void
  placeholder = 'Start typing...',
  minHeight = '400px',
  maxHeight = '800px',
  showWordCount = true,
  wordLimit = 20000,
  hardWordLimit = false, // when true, block input past the limit
  disabled = false,
  onExceedsLimit,        // (exceeds: boolean) => void
  onImageExtracted,      // (file: File) => Promise<string>
}, ref) => {
  const [wordCount, setWordCount] = useState(0);
  const [exceedsLimit, setExceedsLimit] = useState(false);
  const [activeFormats, setActiveFormats] = useState({});
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [showTableMenu, setShowTableMenu] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [showFontMenu, setShowFontMenu] = useState(false);
  const [showSizeMenu, setShowSizeMenu] = useState(false);
  const [showHeadingMenu, setShowHeadingMenu] = useState(false);
  const [showLineHeightMenu, setShowLineHeightMenu] = useState(false);
  const [showSpacingMenu, setShowSpacingMenu] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showPasteOptions, setShowPasteOptions] = useState(false);
  const [pendingPasteData, setPendingPasteData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [textColor, setTextColor] = useState('#000000');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoRedo, setIsUndoRedo] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Image URL resolver. Callers may override upload behavior via onImageExtracted;
  // otherwise images are uploaded to the server (/api/uploads/editor-image) and
  // the returned URL is inserted into the editor.
  const uploadImage = onImageExtracted || uploadEditorImage;

  const contentRef = useRef(null);
  const fileInputRef = useRef(null);
  const colorPickerRef = useRef(null);
  const bgColorPickerRef = useRef(null);
  const tableMenuRef = useRef(null);
  const linkInputRef = useRef(null);
  const fontMenuRef = useRef(null);
  const sizeMenuRef = useRef(null);
  const headingMenuRef = useRef(null);
  const lineHeightMenuRef = useRef(null);
  const spacingMenuRef = useRef(null);

  const fontFamilies = ['Arial', 'Verdana', 'Times New Roman', 'Courier New', 'Georgia', 'Impact', 'Comic Sans MS', 'Monospace'];
  const fontSizes = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];
  const lineHeights = [1.0, 1.15, 1.2, 1.3, 1.5, 1.6, 1.8, 2.0, 2.5];
  const spacingValues = [0, 2, 4, 6, 8, 12, 16, 20, 24, 32];

  const colors = [
    '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3', '#ffffff',
    '#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#0000ff', '#9900ff', '#ff00ff',
    '#000080', '#800080', '#4b0082', '#ee82ee', '#ffa500', '#ffc0cb', '#a52a2a', '#808080', '#a9a9a9', '#d3d3d3'
  ];

  const headings = [
    { label: 'Normal', tag: '<p>' },
    { label: 'H1', tag: '<h1>' },
    { label: 'H2', tag: '<h2>' },
    { label: 'H3', tag: '<h3>' },
    { label: 'H4', tag: '<h4>' }
  ];

  // --------------------- HELPERS ---------------------
  const getPlainText = (html) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html || '';
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  const countWords = (text) => {
    if (!text) return 0;
    const t = String(text).trim();
    return t ? t.split(/\s+/).length : 0;
  };

  const getWordCount = (html) => countWords(getPlainText(html));

  const base64ToBlob = (b64, mime) => {
    const byteChars = atob(b64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    return new Blob([new Uint8Array(byteNumbers)], { type: mime });
  };

  /**
   * Pull image files out of a paste event. Browsers expose clipboard images
   * via clipboardData.files (Chrome/Edge/Safari) or clipboardData.items
   * (Firefox, some Chrome versions). Returns File[].
   */
  const getPastedImageFiles = (e) => {
    const files = [];

    if (e.clipboardData?.files && e.clipboardData.files.length > 0) {
      for (const f of e.clipboardData.files) {
        if (f.type && f.type.startsWith('image/')) files.push(f);
      }
    }

    if (files.length === 0 && e.clipboardData?.items) {
      for (const item of e.clipboardData.items) {
        if (item.kind === 'file' && item.type && item.type.startsWith('image/')) {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
    }

    return files;
  };

  /**
   * Restore the caret to the end of the editor. Used after a hard-limit
   * rollback so the user can continue deleting.
   */
  const restoreCursorToEnd = () => {
    if (!contentRef.current) return;
    const range = document.createRange();
    range.selectNodeContents(contentRef.current);
    range.collapse(false);
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  /**
   * Roll back the editor to the previous history entry. Used by the
   * hard-limit enforcement when a change would push past the limit.
   */
  const rollbackToPrevious = useCallback(() => {
    if (!contentRef.current) return;
    const previous = history[historyIndex] ?? '';
    contentRef.current.innerHTML = previous;
    restoreCursorToEnd();

    // Re-sync derived state without re-entering handleContentChange
    const words = getWordCount(previous);
    setWordCount(words);
    const exceeds = words > wordLimit;
    setExceedsLimit(exceeds);
    onExceedsLimit?.(exceeds);
    onWordCountChange?.(words);
  }, [history, historyIndex, wordLimit, onExceedsLimit, onWordCountChange]);

  const updateWordCount = useCallback(() => {
    if (!contentRef.current) return;
    const content = contentRef.current.innerHTML;
    const words = getWordCount(content);

    setWordCount(words);

    const exceeds = words > wordLimit;
    setExceedsLimit(exceeds);
    onExceedsLimit?.(exceeds);
    onWordCountChange?.(words);

    onHtmlChange?.(content);
  }, [onWordCountChange, onHtmlChange, onExceedsLimit, wordLimit]);

  const handleContentChange = useCallback(() => {
    if (!contentRef.current || isUndoRedo) return;

    // ---- Hard limit enforcement (post-change safety net) ----
    if (hardWordLimit) {
      const words = getWordCount(contentRef.current.innerHTML);
      if (words > wordLimit) {
        // We only roll back if the previous entry is within the limit.
        // If the user somehow got over the limit earlier (e.g., pasted
        // before hard limit was enabled), rolling back would loop.
        const previous = history[historyIndex] ?? '';
        const previousWords = getWordCount(previous);
        if (previousWords <= wordLimit) {
          rollbackToPrevious();
          return;
        }
      }
    }

    const content = contentRef.current.innerHTML;
    updateWordCount();
    onChange?.(content);
    if (content !== history[historyIndex]) {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(content);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  }, [
    onChange, history, historyIndex, isUndoRedo, updateWordCount,
    hardWordLimit, wordLimit, rollbackToPrevious
  ]);

  /**
   * Block the change before the browser mutates the DOM. This is the
   * cleanest enforcement point: it prevents the flash of over-limit
   * content and works for typing, pasting, and drop insertion.
   */
  const handleBeforeInput = useCallback((e) => {
    if (!hardWordLimit || !contentRef.current) return;

    // Let the browser do its thing for deletions, history, etc.
    const deleteTypes = new Set([
      'deleteContentBackward', 'deleteContentForward', 'deleteByCut',
      'deleteWordBackward', 'deleteWordForward', 'deleteSoftLineBackward',
      'deleteSoftLineForward', 'deleteHardLineBackward', 'deleteHardLineForward',
      'deleteByDrag', 'historyUndo', 'historyRedo'
    ]);
    if (deleteTypes.has(e.inputType)) return;

    // Estimate the prospective text after this input.
    let inserted = '';
    if (e.inputType === 'insertText' || e.inputType === 'insertCompositionText') {
      inserted = e.data || '';
    } else if (e.inputType === 'insertFromPaste' || e.inputType === 'insertFromDrop') {
      // We can't easily read clipboard text here; let the paste/drop
      // handlers enforce instead.
      return;
    } else if (e.inputType === 'insertLineBreak' || e.inputType === 'insertParagraph') {
      inserted = ' ';
    } else {
      // Other insert types (formatting, etc.) don't add words — allow.
      return;
    }

    const current = contentRef.current.innerHTML;
    const currentWords = getWordCount(current);

    // Approximate prospective count: current + word-delta of inserted string.
    const deltaWords = countWords(inserted);
    // If we're at the limit and the new text is non-empty, block.
    // (Handles single-space inserts when already at limit.)
    if (currentWords >= wordLimit && inserted.trim().length > 0) {
      e.preventDefault();
      setExceedsLimit(true);
      onExceedsLimit?.(true);
      return;
    }
    // If inserting a large chunk would push past the limit, block it too.
    if (currentWords + deltaWords > wordLimit) {
      e.preventDefault();
      setExceedsLimit(true);
      onExceedsLimit?.(true);
      return;
    }
  }, [hardWordLimit, wordLimit, onExceedsLimit]);

  // --------------------- INIT / SYNC ---------------------
  useEffect(() => {
    if (contentRef.current && value !== undefined && !isInitialized) {
      contentRef.current.innerHTML = value || '';
      setHistory([value || '']);
      setHistoryIndex(0);
      setIsInitialized(true);
      updateWordCount();
    }
  }, [value, isInitialized, updateWordCount]);

  useEffect(() => {
    if (contentRef.current && value !== undefined && !isUndoRedo &&
        contentRef.current.innerHTML !== value) {
      contentRef.current.innerHTML = value;
      updateWordCount();
    }
  }, [value, isUndoRedo, updateWordCount]);

  // --------------------- UNDO / REDO ---------------------
  const handleUndo = useCallback(() => {
    if (historyIndex > 0 && contentRef.current) {
      setIsUndoRedo(true);
      const newIndex = historyIndex - 1;
      const content = history[newIndex];
      contentRef.current.innerHTML = content;
      setHistoryIndex(newIndex);
      updateWordCount();
      onChange?.(content);
      setTimeout(() => setIsUndoRedo(false), 0);
    }
  }, [history, historyIndex, onChange, updateWordCount]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1 && contentRef.current) {
      setIsUndoRedo(true);
      const newIndex = historyIndex + 1;
      const content = history[newIndex];
      contentRef.current.innerHTML = content;
      setHistoryIndex(newIndex);
      updateWordCount();
      onChange?.(content);
      setTimeout(() => setIsUndoRedo(false), 0);
    }
  }, [history, historyIndex, onChange, updateWordCount]);

  // --------------------- CLICK OUTSIDE ---------------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColorPicker && colorPickerRef.current && !colorPickerRef.current.contains(event.target)) setShowColorPicker(false);
      if (showBgColorPicker && bgColorPickerRef.current && !bgColorPickerRef.current.contains(event.target)) setShowBgColorPicker(false);
      if (showTableMenu && tableMenuRef.current && !tableMenuRef.current.contains(event.target)) setShowTableMenu(false);
      if (showLinkInput && linkInputRef.current && !linkInputRef.current.contains(event.target)) { setShowLinkInput(false); setLinkUrl(''); }
      if (showFontMenu && fontMenuRef.current && !fontMenuRef.current.contains(event.target)) setShowFontMenu(false);
      if (showSizeMenu && sizeMenuRef.current && !sizeMenuRef.current.contains(event.target)) setShowSizeMenu(false);
      if (showHeadingMenu && headingMenuRef.current && !headingMenuRef.current.contains(event.target)) setShowHeadingMenu(false);
      if (showLineHeightMenu && lineHeightMenuRef.current && !lineHeightMenuRef.current.contains(event.target)) setShowLineHeightMenu(false);
      if (showSpacingMenu && spacingMenuRef.current && !spacingMenuRef.current.contains(event.target)) setShowSpacingMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColorPicker, showBgColorPicker, showTableMenu, showLinkInput, showFontMenu,
      showSizeMenu, showHeadingMenu, showLineHeightMenu, showSpacingMenu]);

  // =====================================================================
  // NUCLEAR SANITIZER
  // =====================================================================
  const sanitizePastedHtml = useCallback((rawHtml) => {
    if (!rawHtml) return '';

    const preStripped = rawHtml
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/<!\[if[^\]]*\]>[\s\S]*?<!\[endif\]>/gi, '')
      .replace(/<!\[endif\]>/gi, '')
      .replace(/<script\b[\s\S]*?<\/script>/gi, '')
      .replace(/<style\b[\s\S]*?<\/style>/gi, '')
      .replace(/<title\b[\s\S]*?<\/title>/gi, '')
      .replace(/<xml\b[\s\S]*?<\/xml>/gi, '')
      .replace(/<(meta|link|base)\b[^>]*\/?>/gi, '')
      .replace(/<\/?(o|w|v|m|st1|st2|st3|x)\b[^>]*>/gi, '')
      .replace(/<o:p\b[^>]*>[\s\S]*?<\/o:p>/gi, '&nbsp;')
      .replace(/<o:p\b[^>]*\/?>/gi, '&nbsp;');

    const doc = document.implementation.createHTMLDocument('paste-sandbox');
    doc.body.innerHTML = preStripped;

    DANGEROUS_TAGS.forEach(tag => {
      doc.body.querySelectorAll(tag).forEach(el => el.remove());
    });

    const allEls = Array.from(doc.body.querySelectorAll('*'));
    allEls.forEach(el => {
      const tag = el.tagName;

      const allowedAttrs = new Set(['colspan', 'rowspan', 'href', 'src', 'alt', 'title', 'target', 'align', 'valign']);
      Array.from(el.attributes).forEach(attr => {
        const name = attr.name.toLowerCase();
        if (name === 'style') return;
        if (name === 'bgcolor') return;
        if (!allowedAttrs.has(name)) {
          el.removeAttribute(attr.name);
        }
      });

      if (tag === 'A' && el.hasAttribute('href')) {
        const href = el.getAttribute('href') || '';
        if (/^\s*javascript:/i.test(href) || /^\s*vbscript:/i.test(href)) {
          el.removeAttribute('href');
        } else {
          el.setAttribute('target', '_blank');
          el.setAttribute('rel', 'noopener noreferrer');
        }
      }

      if (tag === 'IMG') {
        const src = el.getAttribute('src') || '';
        if (src.startsWith('data:')) {
          const ph = doc.createElement('span');
          ph.className = 'rte-image-placeholder';
          ph.textContent = '[pasted image]';
          ph.setAttribute('data-pasted-image', src);
          el.replaceWith(ph);
          return;
        }
      }

      const styleObj = parseStyleAttr(el.getAttribute('style') || '');

      if (styleObj['color']) {
        const c = extractExactColor(styleObj['color']);
        if (c) styleObj['color'] = c; else delete styleObj['color'];
      }

      const bgRaw = styleObj['background-color']
                  || styleObj['background']
                  || styleObj['mso-shading']
                  || styleObj['mso-highlight'];
      if (bgRaw) {
        const c = extractExactColor(bgRaw);
        if (c) {
          styleObj['background-color'] = c;
          if (isDarkColor(c)) styleObj['color'] = '#ffffff';
        }
      }
      delete styleObj['background'];
      delete styleObj['mso-shading'];
      delete styleObj['mso-highlight'];

      if (el.hasAttribute('bgcolor')) {
        const c = extractExactColor(el.getAttribute('bgcolor'));
        if (c) {
          styleObj['background-color'] = c;
          if (isDarkColor(c)) styleObj['color'] = '#ffffff';
        }
        el.removeAttribute('bgcolor');
      }

      if (el.hasAttribute('align')) {
        styleObj['text-align'] = el.getAttribute('align');
        el.removeAttribute('align');
      }

      Object.keys(styleObj).forEach(k => {
        if (k.startsWith('mso-')) delete styleObj[k];
      });

      const cleanStyle = buildStyleString(styleObj);
      if (cleanStyle) el.setAttribute('style', cleanStyle);
      else el.removeAttribute('style');

      if ((tag === 'SPAN' || tag === 'DIV') &&
          !el.getAttribute('style') &&
          !el.className &&
          el.attributes.length === 0) {
        if (el.childNodes.length === 0) el.remove();
      }
    });

    doc.body.querySelectorAll('table').forEach(table => {
      const tStyle = parseStyleAttr(table.getAttribute('style') || '');
      tStyle['border-collapse'] = 'collapse';
      if (!tStyle['width']) tStyle['width'] = '100%';
      table.setAttribute('style', buildStyleString(tStyle));

      table.querySelectorAll('td, th').forEach(cell => {
        const cStyle = parseStyleAttr(cell.getAttribute('style') || '');
        if (!cStyle['border']) cStyle['border'] = '1px solid #ddd';
        if (!cStyle['padding']) cStyle['padding'] = '8px';
        if (!cStyle['vertical-align']) cStyle['vertical-align'] = 'top';
        cell.setAttribute('style', buildStyleString(cStyle));
      });
    });

    doc.body.querySelectorAll('b').forEach(b => {
      const strong = doc.createElement('strong');
      strong.innerHTML = b.innerHTML;
      if (b.getAttribute('style')) strong.setAttribute('style', b.getAttribute('style'));
      b.replaceWith(strong);
    });
    doc.body.querySelectorAll('i').forEach(i => {
      const em = doc.createElement('em');
      em.innerHTML = i.innerHTML;
      if (i.getAttribute('style')) em.setAttribute('style', i.getAttribute('style'));
      i.replaceWith(em);
    });

    return doc.body.innerHTML
      .replace(/>\s+</g, '><')
      .replace(/(<p[^>]*>(&nbsp;|\s|<br\s*\/?>)*<\/p>){2,}/gi, '<p>&nbsp;</p>')
      .replace(/^(<br\s*\/?>)+/i, '')
      .replace(/(<br\s*\/?>)+$/i, '')
      .trim();
  }, []);

  const processPastedImages = useCallback(async (html) => {
    if (!html.includes('data-pasted-image')) return html;

    const doc = document.implementation.createHTMLDocument('img-swap');
    doc.body.innerHTML = html;

    const placeholders = Array.from(doc.body.querySelectorAll('[data-pasted-image]'));
    if (placeholders.length === 0) return html;

    await Promise.all(placeholders.map(async (ph) => {
      const dataUrl = ph.getAttribute('data-pasted-image');
      if (!dataUrl) {
        ph.textContent = '[image removed]';
        return;
      }
      try {
        const [meta, b64] = dataUrl.split(',');
        const mime = (meta.match(/data:([^;]+)/) || [])[1] || 'image/png';
        const blob = base64ToBlob(b64, mime);
        const file = new File([blob], `pasted-${Date.now()}.png`, { type: mime });

        let url;
        try {
          url = await uploadImage(file);
        } catch (uploadErr) {
          console.warn('Pasted image upload failed, embedding inline', uploadErr);
          url = dataUrl;
        }

        const img = doc.createElement('img');
        img.setAttribute('src', url);
        img.setAttribute('alt', 'pasted image');
        img.setAttribute('style', 'max-width:100%;height:auto;');
        ph.replaceWith(img);
      } catch (err) {
        console.error('Pasted image processing failed', err);
        ph.textContent = '[image removed]';
      }
    }));

    return doc.body.innerHTML;
  }, [uploadImage]);

  // --------------------- EXEC / FORMAT ACTIONS ---------------------
  const execCommand = (cmd, val = null) => {
    if (!contentRef.current) return;
    contentRef.current.focus();
    document.execCommand(cmd, false, val);
    setTimeout(() => handleContentChange(), 10);
  };

  const applyTextColor = (color) => {
    setTextColor(color);
    execCommand('foreColor', color);
    setShowColorPicker(false);
  };

  const applyBackgroundColor = (color) => {
    execCommand('backColor', color);
    setShowBgColorPicker(false);
  };

  const applyLineHeight = (lineHeight) => {
    if (!contentRef.current) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const blocks = new Set();

    const findBlocks = (node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName;
        if (['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE', 'PRE', 'TD', 'TH'].includes(tag)) {
          blocks.add(node);
          return;
        }
        node.childNodes.forEach(findBlocks);
      } else if (node.nodeType === Node.TEXT_NODE) {
        const parentBlock = node.parentElement?.closest(
          'p, div, h1, h2, h3, h4, h5, h6, li, blockquote, pre, td, th'
        );
        if (parentBlock && contentRef.current.contains(parentBlock)) blocks.add(parentBlock);
      }
    };

    const walker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT
    );
    findBlocks(range.commonAncestorContainer);
    let n;
    while ((n = walker.nextNode())) {
      if (range.intersectsNode(n)) findBlocks(n);
    }

    if (blocks.size === 0) {
      const startBlock = selection.anchorNode?.parentElement?.closest(
        'p, div, h1, h2, h3, h4, h5, h6, li, blockquote, pre, td, th'
      );
      if (startBlock && contentRef.current.contains(startBlock)) blocks.add(startBlock);
    }

    blocks.forEach(block => { block.style.lineHeight = String(lineHeight); });

    setShowLineHeightMenu(false);
    handleContentChange();
  };

  const applySpacing = (type, value) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      let element = range.startContainer;
      while (element && element !== contentRef.current &&
             !['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI'].includes(element.tagName)) {
        element = element.parentNode;
      }
      if (element && element !== contentRef.current) {
        if (type === 'top') element.style.marginTop = `${value}px`;
        else if (type === 'bottom') element.style.marginBottom = `${value}px`;
        handleContentChange();
      }
    }
  };

  // --------------------- IMAGE UPLOAD (TOOLBAR) ---------------------
  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setIsUploading(true);
      try {
        const url = await uploadImage(file);
        execCommand('insertHTML',
          `<img src="${url}" alt="${file.name}" style="max-width:100%;height:auto;" />`);
      } catch (err) {
        console.error('Image upload failed, embedding as data URL instead', err);
        const reader = new FileReader();
        reader.onload = (event) => {
          execCommand('insertHTML',
            `<img src="${event.target.result}" alt="${file.name}" style="max-width:100%;height:auto;" />`);
        };
        reader.readAsDataURL(file);
      } finally {
        setIsUploading(false);
      }
    };
    input.click();
  };

  // =====================================================================
  // PASTE HANDLER
  // =====================================================================
  const handlePaste = useCallback(async (e) => {
    e.preventDefault();

    const text = e.clipboardData.getData('text/plain');

    // ---- Hard limit: block paste that would exceed the limit ----
    if (hardWordLimit && contentRef.current) {
      const currentWords = getWordCount(contentRef.current.innerHTML);
      const pastedWords = countWords(text);
      if (currentWords + pastedWords > wordLimit) {
        setExceedsLimit(true);
        onExceedsLimit?.(true);
        return;
      }
    }

    // ---- 1. Image files take priority ----
    const imageFiles = getPastedImageFiles(e);
    if (imageFiles.length > 0) {
      setIsUploading(true);
      try {
        const results = [];
        for (const file of imageFiles) {
          try {
            const url = await uploadImage(file);
            results.push({ url, name: file.name || 'pasted-image' });
          } catch (err) {
            console.error('Pasted image upload failed', err);
          }
        }

        if (results.length > 0) {
          const html = results.map(
            ({ url, name }) =>
              `<img src="${url}" alt="${name}" style="max-width:100%;height:auto;" />`
          ).join('');
          document.execCommand('insertHTML', false, html);
          setTimeout(() => handleContentChange(), 10);
        } else {
          document.execCommand(
            'insertHTML',
            false,
            `<span class="rte-image-placeholder">[image upload failed]</span>`
          );
          setTimeout(() => handleContentChange(), 10);
        }
      } finally {
        setIsUploading(false);
      }
      return;
    }

    // ---- 2. HTML paste ----
    const html = e.clipboardData.getData('text/html');

    if (html) {
      const sanitized = sanitizePastedHtml(html);

      const hasRichContent = sanitized &&
        (/<table|<img|<h[1-6]|<ul|<ol|<blockquote|<pre|<code|<a\s/i.test(sanitized) ||
         /style="/.test(sanitized) ||
         /rte-image-placeholder|data-pasted-image/.test(sanitized));

      const looksLikeOffice = /mso-|Microsoft-com|LibreOffice|bgcolor|highlight|<\w+:\w+/i.test(html);

      if (looksLikeOffice || /<table/i.test(html)) {
        setPendingPasteData({ sanitized, text, rawHasOffice: looksLikeOffice });
        setShowPasteOptions(true);
      } else if (hasRichContent) {
        const withImages = await processPastedImages(sanitized);
        document.execCommand('insertHTML', false, withImages);
        setTimeout(() => handleContentChange(), 10);
      } else {
        document.execCommand('insertText', false, text.trim());
        setTimeout(() => handleContentChange(), 10);
      }
      return;
    }

    // ---- 3. Plain text ----
    document.execCommand('insertText', false, text.trim());
    setTimeout(() => handleContentChange(), 10);
  }, [
    sanitizePastedHtml, processPastedImages, handleContentChange,
    uploadImage, hardWordLimit, wordLimit, onExceedsLimit
  ]);

  // =====================================================================
  // DRAG & DROP
  // =====================================================================
  const handleDragOver = useCallback((e) => {
    if (disabled) return;
    const hasFiles = Array.from(e.dataTransfer?.types || []).includes('Files');
    if (!hasFiles) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    if (disabled) return;
    const files = Array.from(e.dataTransfer?.files || []).filter(
      f => f.type && f.type.startsWith('image/')
    );
    if (files.length === 0) return;
    e.preventDefault();
    setIsDragging(false);
    setIsUploading(true);
    try {
      const html = [];
      for (const file of files) {
        try {
          const url = await uploadImage(file);
          html.push(
            `<img src="${url}" alt="${file.name}" style="max-width:100%;height:auto;" />`
          );
        } catch (err) {
          console.error('Dropped image upload failed', err);
          html.push(`<span class="rte-image-placeholder">[image upload failed]</span>`);
        }
      }
      if (html.length > 0) {
        if (document.caretRangeFromPoint) {
          const range = document.caretRangeFromPoint(e.clientX, e.clientY);
          if (range && contentRef.current.contains(range.startContainer)) {
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }
        document.execCommand('insertHTML', false, html.join(''));
        setTimeout(() => handleContentChange(), 10);
      }
    } finally {
      setIsUploading(false);
    }
  }, [disabled, uploadImage, handleContentChange]);

  // --------------------- WORD IMPORT ---------------------
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    try {
      if (!window.mammoth) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/mammoth@1.4.2/mammoth.browser.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      const reader = new FileReader();
      reader.onload = async (event) => {
        const imageJobs = [];
        let imageCounter = 0;

        const result = await window.mammoth.convertToHtml(
          { arrayBuffer: event.target.result },
          {
            styleMap: [
              "p[style-name='Title'] => h1.title",
              "p[style-name='Subtitle'] => h2.subtitle",
              "p[style-name='Heading 1'] => h1",
              "p[style-name='Heading 2'] => h2",
              "p[style-name='Heading 3'] => h3",
              "r[style-name='Strong'] => strong",
              "r[style-name='Emphasis'] => em",
              "table => table", "b => strong", "i => em"
            ],
            includeDefaultStyleMap: true,
            preserveStyles: true,
            convertImage: window.mammoth.images.imgElement((image) => {
              const id = `__RTE_IMG_${imageCounter++}__`;
              const promise = image.read('base64').then(async (buffer) => {
                const blob = base64ToBlob(buffer, image.contentType);
                const imgFile = new File([blob], `word-image-${id}.png`, { type: image.contentType });
                const url = await uploadImage(imgFile);
                return { id, url };
              });
              imageJobs.push({ id, promise });
              return { src: '' };
            })
          }
        );

        let cleaned = sanitizePastedHtml(result.value);

        await Promise.all(
          imageJobs.map(async ({ promise }) => {
            try {
              const { url } = await promise;
              cleaned = cleaned.replace(/<img\b[^>]*>/,
                `<img src="${url}" alt="imported image" style="max-width:100%;height:auto;" />`);
            } catch (err) {
              console.error('Word image upload failed', err);
              cleaned = cleaned.replace(/<img\b[^>]*>/,
                `<span class="rte-image-placeholder">[image could not be uploaded]</span>`);
            }
          })
        );

        cleaned = cleaned.replace(/<img\b[^>]*src=""[^>]*>/gi,
          `<span class="rte-image-placeholder">[image removed]</span>`);
        cleaned = cleaned.replace(/<img\b[^>]*>/gi,
          `<span class="rte-image-placeholder">[image removed]</span>`);

        // Hard limit: if importing the Word doc would exceed the limit, reject.
        if (hardWordLimit && contentRef.current) {
          const currentWords = getWordCount(contentRef.current.innerHTML);
          const importedWords = getWordCount(cleaned);
          if (currentWords + importedWords > wordLimit) {
            setExceedsLimit(true);
            onExceedsLimit?.(true);
            setIsUploading(false);
            return;
          }
        }

        execCommand('insertHTML', cleaned);
        setIsUploading(false);
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      console.error('Error parsing document:', err);
      setIsUploading(false);
    }
    e.target.value = '';
  };

  // --------------------- INSERT HELPERS ---------------------
  const insertLink = () => {
    if (!linkUrl) return;
    const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
    execCommand('createLink', url);
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const insertTable = (rows, cols) => {
    let html = '<table style="width:100%;border-collapse:collapse;">';
    for (let i = 0; i < rows; i++) {
      html += '<tr>';
      for (let j = 0; j < cols; j++) html += '<td style="border:1px solid #ddd;padding:8px;">&nbsp;</td>';
      html += '</tr>';
    }
    html += '</table>';
    execCommand('insertHTML', html);
    setShowTableMenu(false);
  };

  const insertHeading = (tag) => {
    execCommand('formatBlock', tag);
    setShowHeadingMenu(false);
  };

  // --------------------- ACTIVE FORMATS ---------------------
  const checkActiveFormats = useCallback(() => {
    if (!contentRef.current) return;
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      justifyLeft: document.queryCommandState('justifyLeft'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyRight: document.queryCommandState('justifyRight'),
      justifyFull: document.queryCommandState('justifyFull'),
      insertOrderedList: document.queryCommandState('insertOrderedList'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      formatBlock: document.queryCommandValue('formatBlock')
    });
  }, []);

  useEffect(() => {
    const handleSelectionChange = () => checkActiveFormats();
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [checkActiveFormats]);

  // --------------------- SUB-COMPONENT ---------------------
  const ColorPicker = ({ colors, onSelect, onClose, title, selectedColor }) => (
    <div ref={colorPickerRef} className="absolute top-full left-0 z-50 mt-1 p-2 bg-white border rounded-lg shadow-xl">
      <div className="flex justify-between items-center mb-2 pb-1 border-b">
        <span className="text-xs font-medium text-gray-600">{title}</span>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
      </div>
      <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto">
        {colors.map((color) => (
          <button type="button" key={color} onClick={() => onSelect(color)}
            className={`w-6 h-6 rounded border hover:scale-110 transition-transform ${selectedColor === color ? 'ring-2 ring-blue-500' : ''}`}
            style={{ backgroundColor: color }} title={color} />
        ))}
      </div>
    </div>
  );

  // --------------------- RENDER ---------------------
  return (
    <div className="flex flex-col border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
      <style>{editorStyles}</style>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-1.5 bg-white border-b border-gray-200">
        <button type="button" onClick={handleUndo} disabled={historyIndex <= 0} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30" title="Undo"><Undo2 size={16} className="text-gray-600" /></button>
        <button type="button" onClick={handleRedo} disabled={historyIndex >= history.length - 1} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30" title="Redo"><Redo2 size={16} className="text-gray-600" /></button>
        <div className="w-px h-6 bg-gray-200 mx-1" />

        <div className="relative">
          <button type="button" onClick={() => setShowHeadingMenu(!showHeadingMenu)} className="p-1.5 rounded hover:bg-gray-100 flex items-center gap-1"><HeadingIcon size={14} className="text-gray-600" /><ChevronDown size={12} className="text-gray-500" /></button>
          {showHeadingMenu && <div ref={headingMenuRef} className="absolute top-full left-0 z-50 mt-1 bg-white border rounded-lg shadow-lg min-w-[100px]">{headings.map((h, i) => <button type="button" key={i} onClick={() => insertHeading(h.tag)} className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 ${activeFormats.formatBlock === h.tag ? 'bg-gray-100 text-blue-600' : ''}`}>{h.label}</button>)}</div>}
        </div>
        <div className="w-px h-6 bg-gray-200 mx-1" />

        <div className="relative">
          <button type="button" onClick={() => setShowFontMenu(!showFontMenu)} className="p-1.5 rounded hover:bg-gray-100 flex items-center gap-1"><Type size={14} className="text-gray-600" /><ChevronDown size={12} className="text-gray-500" /></button>
          {showFontMenu && <div ref={fontMenuRef} className="absolute top-full left-0 z-50 mt-1 bg-white border rounded-lg shadow-lg min-w-[150px] max-h-60 overflow-y-auto">{fontFamilies.map(font => <button type="button" key={font} onClick={() => execCommand('fontName', font)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50" style={{ fontFamily: font }}>{font}</button>)}</div>}
        </div>

        <div className="relative">
          <button type="button" onClick={() => setShowSizeMenu(!showSizeMenu)} className="p-1.5 rounded hover:bg-gray-100 flex items-center gap-1"><span className="text-xs font-medium text-gray-600">12</span><ChevronDown size={12} className="text-gray-500" /></button>
          {showSizeMenu && <div ref={sizeMenuRef} className="absolute top-full left-0 z-50 mt-1 bg-white border rounded-lg shadow-lg min-w-[70px] max-h-60 overflow-y-auto">{fontSizes.map(size => <button type="button" key={size} onClick={() => execCommand('fontSize', fontSizes.indexOf(size) + 1)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50">{size}px</button>)}</div>}
        </div>
        <div className="w-px h-6 bg-gray-200 mx-1" />

        <button type="button" onClick={() => execCommand('bold')} className={`p-1.5 rounded hover:bg-gray-100 ${activeFormats.bold ? 'bg-gray-200' : ''}`}><Bold size={16} className="text-gray-600" /></button>
        <button type="button" onClick={() => execCommand('italic')} className={`p-1.5 rounded hover:bg-gray-100 ${activeFormats.italic ? 'bg-gray-200' : ''}`}><Italic size={16} className="text-gray-600" /></button>
        <button type="button" onClick={() => execCommand('underline')} className={`p-1.5 rounded hover:bg-gray-100 ${activeFormats.underline ? 'bg-gray-200' : ''}`}><Underline size={16} className="text-gray-600" /></button>
        <button type="button" onClick={() => execCommand('strikeThrough')} className={`p-1.5 rounded hover:bg-gray-100 ${activeFormats.strikeThrough ? 'bg-gray-200' : ''}`}><Strikethrough size={16} className="text-gray-600" /></button>
        <div className="w-px h-6 bg-gray-200 mx-1" />

        <div className="relative"><button type="button" onClick={() => setShowColorPicker(!showColorPicker)} className="p-1.5 rounded hover:bg-gray-100"><div className="w-4 h-4 rounded border" style={{ backgroundColor: textColor }} /></button>{showColorPicker && <ColorPicker colors={colors} onSelect={applyTextColor} onClose={() => setShowColorPicker(false)} title="Text Color" selectedColor={textColor} />}</div>
        <div className="relative"><button type="button" onClick={() => setShowBgColorPicker(!showBgColorPicker)} className="p-1.5 rounded hover:bg-gray-100"><Highlighter size={16} className="text-gray-600" /></button>{showBgColorPicker && <div ref={bgColorPickerRef} className="absolute top-full left-0 z-50 mt-1 p-2 bg-white border rounded-lg shadow-xl"><div className="flex justify-between items-center mb-2 pb-1 border-b"><span className="text-xs font-medium text-gray-600">Background Color</span><button type="button" onClick={() => setShowBgColorPicker(false)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button></div><div className="grid grid-cols-8 gap-1">{colors.map(color => <button type="button" key={color} onClick={() => applyBackgroundColor(color)} className="w-6 h-6 rounded border hover:scale-110 transition-transform" style={{ backgroundColor: color }} title={color} />)}</div></div>}</div>
        <div className="w-px h-6 bg-gray-200 mx-1" />

        <div className="relative">
          <button type="button" onClick={() => setShowLineHeightMenu(!showLineHeightMenu)} className="p-1.5 rounded hover:bg-gray-100 flex items-center gap-1" title="Line Height"><AlignVerticalJustifyStart size={14} className="text-gray-600" /><ChevronDown size={12} className="text-gray-500" /></button>
          {showLineHeightMenu && <div ref={lineHeightMenuRef} className="absolute top-full left-0 z-50 mt-1 bg-white border rounded-lg shadow-lg min-w-[80px]">{lineHeights.map(lh => <button type="button" key={lh} onClick={() => applyLineHeight(lh)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50">{lh}</button>)}</div>}
        </div>

        <div className="relative">
          <button type="button" onClick={() => setShowSpacingMenu(!showSpacingMenu)} className="p-1.5 rounded hover:bg-gray-100 flex items-center gap-1" title="Paragraph Spacing"><Pilcrow size={14} className="text-gray-600" /><ChevronDown size={12} className="text-gray-500" /></button>
          {showSpacingMenu && <div ref={spacingMenuRef} className="absolute top-full left-0 z-50 mt-1 bg-white border rounded-lg shadow-lg min-w-[120px]"><div className="px-2 py-1 text-xs font-semibold text-gray-500 border-b">Top Margin (px)</div><div className="grid grid-cols-4 gap-1 p-2 max-h-48 overflow-y-auto">{spacingValues.map(val => <button type="button" key={`top-${val}`} onClick={() => applySpacing('top', val)} className="p-1 text-xs bg-gray-100 hover:bg-blue-100 rounded text-center">{val}</button>)}</div><div className="px-2 py-1 text-xs font-semibold text-gray-500 border-t border-b mt-1">Bottom Margin (px)</div><div className="grid grid-cols-4 gap-1 p-2 max-h-48 overflow-y-auto">{spacingValues.map(val => <button type="button" key={`bottom-${val}`} onClick={() => applySpacing('bottom', val)} className="p-1 text-xs bg-gray-100 hover:bg-blue-100 rounded text-center">{val}</button>)}</div></div>}
        </div>

        <div className="w-px h-6 bg-gray-200 mx-1" />

        <button type="button" onClick={() => execCommand('justifyLeft')} className={`p-1.5 rounded hover:bg-gray-100 ${activeFormats.justifyLeft ? 'bg-gray-200' : ''}`}><AlignLeft size={16} className="text-gray-600" /></button>
        <button type="button" onClick={() => execCommand('justifyCenter')} className={`p-1.5 rounded hover:bg-gray-100 ${activeFormats.justifyCenter ? 'bg-gray-200' : ''}`}><AlignCenter size={16} className="text-gray-600" /></button>
        <button type="button" onClick={() => execCommand('justifyRight')} className={`p-1.5 rounded hover:bg-gray-100 ${activeFormats.justifyRight ? 'bg-gray-200' : ''}`}><AlignRight size={16} className="text-gray-600" /></button>
        <button type="button" onClick={() => execCommand('justifyFull')} className={`p-1.5 rounded hover:bg-gray-100 ${activeFormats.justifyFull ? 'bg-gray-200' : ''}`}><AlignJustify size={16} className="text-gray-600" /></button>
        <div className="w-px h-6 bg-gray-200 mx-1" />

        <button type="button" onClick={() => execCommand('insertUnorderedList')} className={`p-1.5 rounded hover:bg-gray-100 ${activeFormats.insertUnorderedList ? 'bg-gray-200' : ''}`}><List size={16} className="text-gray-600" /></button>
        <button type="button" onClick={() => execCommand('insertOrderedList')} className={`p-1.5 rounded hover:bg-gray-100 ${activeFormats.insertOrderedList ? 'bg-gray-200' : ''}`}><ListOrdered size={16} className="text-gray-600" /></button>
        <div className="w-px h-6 bg-gray-200 mx-1" />

        <button type="button" onClick={() => execCommand('formatBlock', '<blockquote>')} className={`p-1.5 rounded hover:bg-gray-100 ${activeFormats.formatBlock === 'blockquote' ? 'bg-gray-200' : ''}`}><Quote size={16} className="text-gray-600" /></button>
        <button type="button" onClick={() => execCommand('formatBlock', '<pre>')} className={`p-1.5 rounded hover:bg-gray-100 ${activeFormats.formatBlock === 'pre' ? 'bg-gray-200' : ''}`}><Code size={16} className="text-gray-600" /></button>
        <div className="w-px h-6 bg-gray-200 mx-1" />

        <button type="button" onClick={() => setShowLinkInput(!showLinkInput)} className="p-1.5 rounded hover:bg-gray-100"><LinkIcon size={16} className="text-gray-600" /></button>
        <button type="button" onClick={handleImageUpload} className="p-1.5 rounded hover:bg-gray-100"><ImageIcon size={16} className="text-gray-600" /></button>
        <div className="relative"><button type="button" onClick={() => setShowTableMenu(!showTableMenu)} className="p-1.5 rounded hover:bg-gray-100"><Table size={16} className="text-gray-600" /></button>{showTableMenu && <div ref={tableMenuRef} className="absolute top-full left-0 z-50 mt-1 p-2 bg-white border rounded-lg shadow-lg min-w-[140px]"><div className="text-xs font-semibold text-gray-500 mb-2 px-1">Insert Table</div><div className="grid grid-cols-3 gap-1">{[2,3,4].map(rows => [2,3,4].map(cols => <button type="button" key={`${rows}x${cols}`} onClick={() => insertTable(rows, cols)} className="p-1 text-xs bg-gray-100 hover:bg-blue-100 rounded text-center">{rows}x{cols}</button>))}</div></div>}</div>
        <button type="button" onClick={() => execCommand('removeFormat')} className="p-1.5 rounded hover:bg-gray-100"><Eraser size={16} className="text-gray-600" /></button>

        <div className="flex-1" />
        <button type="button" onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-md flex items-center gap-2 text-xs font-medium">{isUploading ? <div className="w-3 h-3 border-2 border-white border-t-transparent animate-spin rounded-full" /> : <FileText size={14} />}ATTACH WORD</button>
        <input type="file" ref={fileInputRef} className="hidden" accept=".docx,.doc" onChange={handleFileUpload} />
      </div>

      {showLinkInput && (
        <div ref={linkInputRef} className="flex gap-2 p-2 bg-gray-50 border-b border-gray-200">
          <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="Enter URL (e.g., https://example.com)" className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" autoFocus onKeyDown={(e) => e.key === 'Enter' && insertLink()} />
          <button type="button" onClick={insertLink} className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">Insert</button>
          <button type="button" onClick={() => { setShowLinkInput(false); setLinkUrl(''); }} className="px-4 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300">Cancel</button>
        </div>
      )}

      <div
        ref={contentRef}
        contentEditable={!disabled}
        onBeforeInput={handleBeforeInput}
        onPaste={handlePaste}
        onInput={handleContentChange}
        onKeyUp={handleContentChange}
        onMouseUp={checkActiveFormats}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`rich-text-editor-content p-6 overflow-y-auto ${isDragging ? 'rte-dragover' : ''} ${exceedsLimit && hardWordLimit ? 'rte-limit-reached' : ''}`}
        style={{ minHeight, maxHeight }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />

      {showWordCount && (
        <div className="px-4 py-2 bg-gray-50 border-t text-xs flex justify-between items-center">
          <span className={exceedsLimit ? 'text-red-500 font-medium' : 'text-gray-500'}>
            {wordCount} / {wordLimit} words
            {hardWordLimit && exceedsLimit && (
              <span className="ml-2 text-red-500">(limit reached)</span>
            )}
          </span>
          {exceedsLimit && <span className="text-red-500 font-medium">Exceeds limit!</span>}
        </div>
      )}

      {showPasteOptions && pendingPasteData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[999] p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full text-center">
            <h3 className="text-lg font-semibold mb-2">Preserve Layout?</h3>
            <p className="text-sm text-gray-500 mb-6">Keep original styles, highlights, tables, and background colors?</p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={async () => {
                  const withImages = await processPastedImages(pendingPasteData.sanitized);
                  execCommand('insertHTML', withImages);
                  setShowPasteOptions(false);
                  setPendingPasteData(null);
                }}
                className="w-full py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
              >
                Keep Formatting
              </button>
              <button
                type="button"
                onClick={() => {
                  execCommand('insertText', (pendingPasteData.text || '').trim());
                  setShowPasteOptions(false);
                  setPendingPasteData(null);
                }}
                className="w-full py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
              >
                Strip to Plain Text
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';
export default RichTextEditor;